import { createRouter, createWebHistory } from 'vue-router'
import { getWorkStatisticsDateRangeError } from '@/views/dashboard/userWorkStatistics/workStatisticsDateRange'
import HomeView from '../views/HomeView.vue'
import {
  AuthError,
  clearAuthSession,
  ensureAuthenticated,
  getAuthReturnUrl,
  getAuthVersion,
  isAuthenticationFailure,
} from '@/utils/framework'
import { useGroupStore } from '@/stores/group'
import useAliasesStore from '@/stores/aliases'
import useRouteLoading from '@/hooks/useRouteLoading'
import { getSurveyOptions } from '@/api/commonApi'
import isTopWindow from '@/utils/isTopWindow'

const { start, stop } = useRouteLoading()
const enableRouteLoading = isTopWindow()

const normalizeLegacyTicketHash = (hash: string) => {
  let hashText = hash.slice(1)

  try {
    hashText = decodeURIComponent(hashText)
  } catch {
    return undefined
  }

  const legacyInboxMatch = hashText.match(/^Inbox:\s*(.*)$/i)

  if (!legacyInboxMatch) {
    return undefined
  }

  const keywords = legacyInboxMatch[1]?.trim()

  return keywords ? `#${keywords}` : ''
}
// const redirectIndex: RouteRecordRedirectOption = (to) => {
//   const route = router.getRoutes().find((r) => r.name === to.name)
//
//   const newRouteName = route?.children?.[0]?.name
//
//   if (newRouteName) {
//     return { name: newRouteName, query: { f: to.name } }
//   }
// }

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      children: [
        {
          path: '',
          redirect: (to) => ({ name: 'dashboard', query: to.query, hash: to.hash }),
          meta: { hidden: true },
        },
        {
          path: 'dashboard',
          name: 'home-index',
          meta: { title: 'Dashboard', icon: 'mdi:view-dashboard-variant-outline' },
          component: () => import('@/views/IndexView.vue'),
          children: [
            {
              path: '',
              name: 'dashboard',
              meta: {
                title: 'Team Performance',
                sidebarTitle: 'Overview',
                icon: 'mdi:view-dashboard-variant-outline',
              },
              component: () => import('@/views/dashboard'),
            },
            {
              path: 'issue-details',
              name: 'issue-details',
              meta: { hidden: true, title: 'Issue Details' },
              component: () => import('@/views/dashboard/issueInsights'),
            },
            {
              path: 'unresolved-emails',
              name: 'unresolved-emails',
              meta: {
                title: 'Unresolved Emails',
                sidebarTitle: 'Unresolved Emails',
                icon: 'mdi:email-arrow-right-outline',
              },
              component: () => import('@/views/dashboard/unresolvedEmails'),
            },
            {
              path: 'work-statistics',
              name: 'user-work-statistics',
              meta: {
                title: 'Work Statistics',
                sidebarTitle: 'Work Statistics',
                icon: 'mdi:chart-box-outline',
              },
              component: () => import('@/views/dashboard/userWorkStatistics'),
            },
            {
              path: 'work-statistics/details',
              name: 'work-statistics-details',
              meta: { hidden: true, title: 'Work Statistics Details' },
              beforeEnter: (to) => {
                const firstQueryValue = (value: unknown) =>
                  Array.isArray(value) ? value[0] : value
                const metric = firstQueryValue(to.query.metric)
                const userId = Number(firstQueryValue(to.query.user_id))
                const startDate = new Date(String(firstQueryValue(to.query.start_date) ?? ''))
                const endDate = new Date(String(firstQueryValue(to.query.end_date) ?? ''))
                const hasEventTeamId = Object.prototype.hasOwnProperty.call(
                  to.query,
                  'event_team_id',
                )

                if (
                  !['resolved', 'notes', 'replies'].includes(String(metric)) ||
                  !Number.isInteger(userId) ||
                  userId <= 0 ||
                  !hasEventTeamId ||
                  Number.isNaN(startDate.getTime()) ||
                  Number.isNaN(endDate.getTime()) ||
                  getWorkStatisticsDateRangeError(startDate, endDate) !== null
                ) {
                  return { name: 'user-work-statistics' }
                }

                return true
              },
              component: () =>
                import('@/views/dashboard/userWorkStatistics/WorkStatisticsDetails.vue'),
            },
            {
              path: 'user-work-statistics',
              redirect: (to) => ({ name: 'user-work-statistics', query: to.query, hash: to.hash }),
              meta: { hidden: true },
            },
            {
              path: 'satisfaction-survey',
              name: 'satisfaction-survey',
              meta: {
                title: 'Satisfaction Survey',
                sidebarTitle: 'Satisfaction Survey',
                icon: 'mdi:star-outline',
              },
              beforeEnter: async (to) => {
                const {
                  name,
                  query: { id },
                } = to

                const requestSurveyOptions = getSurveyOptions()

                const sOptions = await requestSurveyOptions()

                if (id && sOptions.some((s) => s.value === +id)) {
                  return true
                }

                return sOptions[0]
                  ? { name, query: { id: sOptions[0].value } }
                  : { name: 'not-found' }
              },
              component: () => import('@/views/dashboard/satisfactionSurvey'),
            },
          ],
        },
        {
          path: 'unresolved-emails',
          redirect: (to) => ({ name: 'unresolved-emails', query: to.query, hash: to.hash }),
          meta: { hidden: true },
        },
        {
          path: 'user-work-statistics',
          redirect: (to) => ({ name: 'user-work-statistics', query: to.query, hash: to.hash }),
          meta: { hidden: true },
        },
        {
          path: 'satisfaction-survey',
          redirect: (to) => ({ name: 'satisfaction-survey', query: to.query, hash: to.hash }),
          meta: { hidden: true },
        },
        {
          path: 'tickets',
          component: () => import('@/views/tickets'),
          beforeEnter: async () => {
            const groupStore = useGroupStore()
            const aliasesStore = useAliasesStore()

            await groupStore.getRemoteData()
            await aliasesStore.getAliasesApi()

            return true
          },
          meta: { title: 'Tickets', icon: 'mdi:email-edit-outline' },
          children: [
            {
              path: '',
              name: 'tickets',
              meta: { title: 'Tickets', icon: 'mdi:email-outline', keepAlive: true },
              component: () => import('@/views/tickets/ticketList'),
            },
            {
              path: 'new',
              name: 'new-ticket',
              meta: { hidden: true, title: 'New Ticket' },
              component: () => import('@/views/tickets/newTicket'),
            },
            {
              path: 'failed-sends',
              name: 'ticket-failed-sends',
              meta: { hidden: true, title: 'Failed sends' },
              component: () => import('@/views/tickets/failedSends'),
            },
            {
              path: ':id',
              name: 'ticket',
              meta: { hidden: true, title: 'Ticket', icon: 'mdi:ticket-outline' },
              component: () => import('@/views/tickets/ticketDetails'),
            },
            {
              path: ':id/forward',
              name: 'ticket-forward',
              meta: { hidden: true, title: 'Forward Ticket' },
              component: () => import('@/views/tickets/newTicket/ForwardTicket.vue'),
            },
          ],
        },
        {
          path: 'email-management',
          redirect: (to, from) =>
            from.name === 'tickets'
              ? { name: 'tickets', hash: to.hash || from.hash }
              : { name: 'tickets', query: to.query, hash: to.hash },
          meta: { hidden: true },
        },
        {
          path: 'email-management/ticket/:id',
          redirect: (to) => ({
            name: 'ticket',
            params: to.params,
            query: to.query,
            hash: to.hash,
          }),
          meta: { hidden: true },
        },
        {
          path: 'email-management/failed-sends',
          redirect: (to) => ({
            name: 'ticket-failed-sends',
            query: to.query,
            hash: to.hash,
          }),
          meta: { hidden: true },
        },
        {
          path: 'email-management/forward',
          redirect: (to) => {
            const query = { ...to.query }
            const legacyTicketId = query.id
            const ticketId = Array.isArray(legacyTicketId) ? legacyTicketId[0] : legacyTicketId

            delete query.id

            return ticketId
              ? {
                  name: 'ticket-forward',
                  params: { id: ticketId },
                  query,
                  hash: to.hash,
                }
              : { name: 'tickets', query, hash: to.hash }
          },
          meta: { hidden: true },
        },
        {
          path: 'email-management/new-email',
          redirect: (to) => ({ name: 'new-ticket', query: to.query, hash: to.hash }),
          meta: { hidden: true },
        },
        {
          path: 'contacts',
          name: 'contacts',
          meta: { title: 'Contacts', icon: 'mdi:contact-outline' },
          redirect: { name: 'contacts-index' },
          children: [
            {
              path: '',
              name: 'contacts-index',
              component: () => import('@/views/contacts/ContactsIndex.vue'),
            },
            {
              path: ':id',
              name: 'contacts-details',
              meta: { title: 'Contacts Details' },
              component: () => import('@/views/contacts/contactsDetails/ContactsDetails.vue'),
              redirect: { name: 'contacts-details-basic-info' },
              children: [
                {
                  path: '',
                  name: 'contacts-details-basic-info',
                  component: () =>
                    import('@/views/contacts/contactsDetails/ContactsDetailsBasicInfo.vue'),
                },
                {
                  path: 'timeline',
                  name: 'contacts-details-timeline',
                  component: () =>
                    import('@/views/contacts/contactsDetails/ContactsDetailsTimeline.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'settings',
          name: 'settings',
          meta: { title: 'Settings', icon: 'mdi:settings' },
          redirect: { name: 'channel-management' },
          component: () => import('@/views/settings/Settings.vue'),
          children: [
            {
              path: 'channel-management',
              name: 'channel-management',
              meta: { title: 'Channel' },
              component: () => import('@/views/settings/channelManagement'),
            },
            {
              path: 'user-management',
              name: 'user-management',
              meta: { title: 'User' },
              component: () => import('@/views/settings/userManagement/UserManagement.vue'),
            },
            {
              path: 'role-management',
              name: 'role-management',
              meta: { title: 'Role' },
              component: () => import('@/views/settings/roleManagement'),
            },
            {
              path: 'auth-link',
              name: 'auth-link',
              meta: { title: 'Authorization Link', hidden: true },
              component: () => import('@/views/authLink'),
            },
            {
              path: 'preset-reply',
              name: 'preset-reply',
              meta: { title: 'Preset Reply' },
              component: () => import('@/views/settings/presetReply'),
            },
            {
              path: 'filters',
              name: 'filters',
              meta: { title: 'Filters' },
              component: () => import('@/views/settings/filters/Filters.vue'),
            },
            {
              path: 'receiving-rules',
              name: 'receiving-rules',
              redirect: { name: 'receiving-rules-index' },
              meta: { title: 'Receiving Rules' },
              children: [
                {
                  path: '',
                  name: 'receiving-rules-index',
                  meta: { hidden: true },
                  component: () =>
                    import('@/views/settings/receivingRules/ReceivingRulesIndex.vue'),
                },
                {
                  path: 'new',
                  name: 'receiving-rules-new',
                  meta: { title: 'New Receiving Rule', hidden: true },
                  component: () =>
                    import('@/views/settings/receivingRules/ReceivingRulesFormView.vue'),
                },
                {
                  path: 'edit',
                  name: 'receiving-rules-edit',
                  meta: { title: 'Edit Receiving Rule', hidden: true },
                  component: () =>
                    import('@/views/settings/receivingRules/ReceivingRulesFormEditView.vue'),
                },
              ],
            },
            {
              path: 'satisfaction-survey',
              name: 'settings-satisfaction-survey',
              meta: { title: 'Satisfaction Survey' },
              redirect: { name: 'settings-satisfaction-survey-index' },
              children: [
                {
                  path: '',
                  name: 'settings-satisfaction-survey-index',
                  meta: { hidden: true },
                  component: () => import('@/views/settings/satisfactionSurvey'),
                },
                {
                  path: 'new',
                  name: 'settings-satisfaction-survey-new',
                  meta: { title: 'New Satisfaction Survey', hidden: true },
                  component: () => import('@/views/settings/satisfactionSurvey/form'),
                },
                {
                  path: 'edit',
                  name: 'settings-satisfaction-survey-edit',
                  meta: { title: 'Edit Satisfaction Survey', hidden: true },
                  component: () =>
                    import(
                      '@/views/settings/satisfactionSurvey/form/SatisfactionSurveyFormEditView.vue'
                    ),
                },
              ],
            },
            {
              path: 'satisfaction-survey-management/:pathMatch(.*)*',
              redirect: (to) => {
                const legacyPath = Array.isArray(to.params.pathMatch)
                  ? to.params.pathMatch.join('/')
                  : String(to.params.pathMatch || '')

                return {
                  path: legacyPath
                    ? `/settings/satisfaction-survey/${legacyPath}`
                    : '/settings/satisfaction-survey',
                  query: to.query,
                  hash: to.hash,
                }
              },
              meta: { hidden: true },
            },
            {
              path: 'ticket-categorization',
              name: 'ticket-categorization',
              meta: { title: 'Ticket Categorization' },
              redirect: { name: 'ticket-categorization-index' },
              children: [
                {
                  path: '',
                  name: 'ticket-categorization-index',
                  meta: { hidden: true },
                  component: () => import('@/views/settings/ticketCategorization'),
                },
                {
                  path: 'new',
                  name: 'ticket-categorization-new',
                  meta: { title: 'New Ticket Category', hidden: true },
                  component: () =>
                    import('@/views/settings/ticketCategorization/TicketCategoryFormView.vue'),
                },
                {
                  path: 'edit',
                  name: 'ticket-categorization-edit',
                  meta: { title: 'Edit Ticket Category', hidden: true },
                  component: () =>
                    import('@/views/settings/ticketCategorization/TicketCategoryFormEditView.vue'),
                },
              ],
            },
            {
              path: 'issue-categories',
              name: 'issue-categories',
              meta: { title: 'Issue Categories' },
              component: () => import('@/views/settings/issueCategories'),
            },
            {
              path: 'ticket-rules',
              name: 'ticket-rules',
              meta: { title: 'Ticket Rules' },
              component: () => import('@/views/settings/ticketRules'),
            },
            {
              path: 'ticket-auto-reply',
              name: 'ticket-auto-reply',
              redirect: { name: 'ticket-auto-reply-index' },
              meta: { title: 'Ticket Auto Reply' },
              children: [
                {
                  path: '',
                  name: 'ticket-auto-reply-index',
                  meta: { hidden: true },
                  component: () =>
                    import('@/views/settings/ticketAutoReply/TicketAutoReplyIndex.vue'),
                },
                {
                  path: 'new',
                  name: 'ticket-auto-reply-new',
                  meta: { title: 'New Auto Reply', hidden: true },
                  component: () =>
                    import('@/views/settings/ticketAutoReply/TicketAutoReplyFormCreateView.vue'),
                },
                {
                  path: 'edit',
                  name: 'ticket-auto-reply-edit',
                  meta: { title: 'Edit Auto Reply', hidden: true },
                  component: () =>
                    import('@/views/settings/ticketAutoReply/TicketAutoReplyFormEditView.vue'),
                },
              ],
            },
          ],
        },
        {
          path: 'system-settings/:pathMatch(.*)*',
          redirect: (to) => {
            const legacyPath = Array.isArray(to.params.pathMatch)
              ? to.params.pathMatch.join('/')
              : String(to.params.pathMatch || '')

            return {
              path: legacyPath ? `/settings/${legacyPath}` : '/settings',
              query: to.query,
              hash: to.hash,
            }
          },
          meta: { hidden: true },
        },
        {
          name: 'middle',
          path: 'middle/:all?',
          meta: { hidden: true },
          component: () => import('@/views/MiddleView.vue'),
        },
      ],
    },
    {
      path: '/satisfaction-survey-form/:token',
      name: 'ssm-form',
      meta: { title: 'Satisfaction Survey', hidden: true },
      component: () => import('@/views/satisfactionSurveySubmit'),
    },
    {
      path: '/verification-failed',
      name: 'verification-failed',
      meta: { title: 'Verification Failed', hidden: true },
      component: () => import('@/views/VerificationFailed.vue'),
    },
    {
      name: 'passport',
      path: '/passport',
      component: () => import('@/views/PassportView.vue'),
    },
    {
      name: 'status',
      path: '/status',
      meta: { title: 'App Service Status', hidden: true },
      component: () => import('@/views/ServiceStatus.vue'),
    },
    {
      name: 'not-found',
      path: '/:pathMatch(.*)*',
      meta: { title: 'Not Found', hidden: true },
      component: () => import('@/views/NotFound.vue'),
    },
  ],
})

router.beforeEach(async (to) => {
  if (to.name === 'tickets') {
    const normalizedHash = normalizeLegacyTicketHash(to.hash)

    if (normalizedHash !== undefined) {
      return {
        name: 'tickets',
        query: to.query,
        hash: normalizedHash,
        replace: true,
      }
    }
  }

  if (enableRouteLoading) {
    start()
  }

  if (['passport', 'status', 'verification-failed', 'ssm-form'].includes(to.name as string)) {
    return true
  }

  // SSO may return to the root URL. Always consume callbacks before business navigation.
  if ((to.query.code && to.query.state) || (to.query.error && to.query.state)) {
    return { name: 'passport', query: to.query, replace: true }
  }

  const version = getAuthVersion()

  try {
    await ensureAuthenticated()
  } catch (error) {
    if (version !== getAuthVersion() || (error instanceof AuthError && error.kind === 'stale')) {
      return false
    }

    const requiresLogin = isAuthenticationFailure(error)
    if (requiresLogin) clearAuthSession()

    return {
      name: 'passport',
      query: {
        redirect: getAuthReturnUrl(to.fullPath),
        reason: requiresLogin ? 'login' : 'unavailable',
      },
      replace: true,
    }
  }

  return version === getAuthVersion()
})

router.afterEach(() => {
  if (enableRouteLoading) {
    stop()
  }
})

export default router
